import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import ResumeEditor from "./ResumeEditor";
import NewCvWizard from "./NewCvWizard";
import { Plus, Copy, Trash2, Star, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

export default function ResumeBuilder({ onLaunchJobWizard, initialResumeId, onSelected }) {
  const confirm = useConfirm();
  const { t } = useT();
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(initialResumeId || null);
  const [wizardOpen, setWizardOpen] = useState(false);

  useEffect(() => {
    if (initialResumeId) {
      setSelectedId(initialResumeId);
      onSelected?.();
    }
  }, [initialResumeId, onSelected]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/resumes");
      setVariants(data);
    } catch {
      toast.error(t("dashLoadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const duplicate = async (id) => {
    try {
      const { data } = await api.post(`/resumes/${id}/duplicate`);
      await load();
      setSelectedId(data.id);
      toast.success(t("rbDuplicated"));
    } catch {
      toast.error(t("rbSaveFailed"));
    }
  };

  const makeDefault = async (id) => {
    try {
      await api.post(`/resumes/${id}/set-default`);
      await load();
      toast.success(t("rbDefaultSet"));
    } catch {
      toast.error(t("rbSaveFailed"));
    }
  };

  const remove = async (id) => {
    if (!(await confirm(t("rbConfirmDelete")))) return;
    try {
      await api.delete(`/resumes/${id}`);
      if (selectedId === id) setSelectedId(null);
      await load();
      toast.success(t("rbDeleted"));
    } catch {
      toast.error(t("dashDeleteFailed"));
    }
  };

  return (
    <div className="space-y-5" data-testid="resume-builder">
      <button
        data-testid="resume-new-cv"
        className="ed-btn"
        onClick={() => setWizardOpen(true)}
      >
        <Plus size={14} strokeWidth={1.5} /> {t("ncwCta")}
      </button>

      {loading ? (
        <p className="ed-eyebrow p-2">{t("loading")}</p>
      ) : variants.length === 0 ? (
        <p className="text-sm text-neutral-500 italic font-serif p-2" data-testid="resume-no-variants">{t("rbNoVariants")}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {variants.map((v, i) => (
              <div
                key={v.id}
                data-testid={`resume-variant-${i}`}
                onClick={() => setSelectedId(v.id)}
                className={`ed-card p-4 cursor-pointer flex items-center justify-between gap-3 transition-colors ${selectedId === v.id ? "border-neutral-900 bg-white" : "hover:border-neutral-400"}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="font-serif text-lg leading-tight truncate">{v.name}</p>
                    {v.is_default && (
                      <span
                        data-testid={`resume-default-badge-${i}`}
                        className="shrink-0 inline-flex items-center gap-1 bg-neutral-900 text-[#F9F8F6] font-mono text-[0.55rem] uppercase tracking-[0.15em] px-2 py-0.5"
                      >
                        <Star size={9} strokeWidth={2} fill="currentColor" /> {t("rbDefault")}
                      </span>
                    )}
                  </div>
                  <p className="font-mono text-[0.65rem] text-neutral-500 mt-0.5 truncate">
                    {v.personal?.full_name || "—"}{v.personal?.title ? ` · ${v.personal.title}` : ""}
                  </p>
                  {v.generated_from_job?.job_title && (
                    <span
                      className="mt-1 font-mono text-[0.55rem] uppercase tracking-[0.12em] border border-neutral-300 text-neutral-700 px-2 py-0.5 inline-flex items-center gap-1 max-w-full truncate"
                      data-testid={`resume-variant-vacancy-badge-${i}`}
                      title={t("cvOvVacancyBadgeTitle", { title: v.generated_from_job.job_title })}
                    >
                      <Briefcase size={9} strokeWidth={1.7} className="shrink-0" />
                      <span className="truncate">{v.generated_from_job.job_title}</span>
                    </span>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                  {!v.is_default && (
                    <button
                      data-testid={`resume-make-default-${i}`}
                      onClick={() => makeDefault(v.id)}
                      className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
                      title={t("rbMakeDefault")}
                    >
                      <Star size={13} strokeWidth={1.5} />
                    </button>
                  )}                  <button
                    data-testid={`resume-duplicate-${i}`}
                    onClick={() => duplicate(v.id)}
                    className="p-2 border border-neutral-300 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors"
                    title={t("rbDuplicate")}
                  >
                    <Copy size={13} strokeWidth={1.5} />
                  </button>
                  <button
                    data-testid={`resume-delete-${i}`}
                    onClick={() => remove(v.id)}
                    className="p-2 border border-neutral-300 hover:border-rose-700 hover:bg-rose-700 hover:text-white transition-colors"
                    title={t("delete")}
                  >
                    <Trash2 size={13} strokeWidth={1.5} />
                  </button>
                </div>
            </div>
          ))}
        </div>
      )}

      {selectedId && (
        <ResumeEditor resumeId={selectedId} onChanged={load} onClose={() => setSelectedId(null)} />
      )}

      {wizardOpen && (
        <NewCvWizard
          variants={variants}
          onClose={() => setWizardOpen(false)}
          onCreated={async (data) => { await load(); setSelectedId(data.id); }}
          onLaunchJobWizard={() => { setWizardOpen(false); onLaunchJobWizard?.(); }}
        />
      )}
    </div>
  );
}
